// Recycler domain: fleet (trucks, drivers, schedules), suppliers and their
// agreements, collection points, labels, facilities, routes, completion flows
// and the pre-order (purchase request) monitor.

import type { FlowType } from '../../types/collection-flow.type';
import { DEMO_OUTLET_ORG_ID, DEMO_RECYCLER_ORG_ID, demoError, hashString, ids, paginate, parseQueryParam, type DemoCtx, type DemoRouteDef } from '../core';
import { ensureDemoData } from '../data';
import { bodyOf, includesText } from '../data/common';
import {
  assignDriver,
  createTruck,
  deleteTruck,
  listTrucks,
  truckForDriver,
  truckRecordById,
  setTruckPosition,
  unassignDriver,
  updateTruck,
} from '../data/fleet';
import {
  FLOW_CONTRACTS,
  FLOW_STEP_TYPES,
  activateFlow,
  archiveAgreementDocument,
  archiveFacility,
  archivePoint,
  archiveRoute,
  archiveSupplier,
  archiveZone,
  attachAgreementDocument,
  createFacility,
  createPoint,
  createRoute,
  createSupplier,
  createZone,
  facilityRecordById,
  listAgreementDocuments,
  listAgreementPrices,
  listFacilityRecords,
  listFlows,
  listPointRecords,
  listRouteRecords,
  listSupplierRecords,
  listZoneRecords,
  resolveFlow,
  routeRecordById,
  saveFlowDraft,
  serializeFacility,
  serializePoint,
  serializeRoute,
  serializeSupplier,
  serializeZone,
  setAgreementPrice,
  setRouteTruck,
  supplierRecordById,
  syncSupplierSites,
  updateFacility,
  updatePoint,
  updateRoute,
  updateSupplier,
  updateZone,
  zoneRecordById,
} from '../data/network';
import { allRuns, runTruckPosition } from '../data/orders';
import { listOrganizations, registerDriverUser, userById, usernameTaken } from '../data/people';
import { clearScheduleDay, isDayOfWeek, organizationSchedule, setScheduleDay, truckSchedule } from '../data/schedule';
import { listContainerRecords, listSites } from '../data/sites';
import { driverHistory, driverName, facilityIncoming, preOrdersMonitor } from '../data/views';

function notFound(what: string) {
  return demoError(404, `${what} not found`);
}

function q(ctx: Pick<DemoCtx, 'query'>, key: string): string | undefined {
  return parseQueryParam(ctx, key);
}

/** Recycler-owned records are only visible to the recycler organization. */
function isRecycler(ctx: DemoCtx): boolean {
  return (q(ctx, 'organization_id') ?? ctx.orgId) === DEMO_RECYCLER_ORG_ID;
}

/** Trucks on a live run report where they are along the route. */
function refreshLivePositions(): void {
  allRuns()
    .filter((r) => r.status === 'in_progress')
    .forEach((r) => {
      const at = runTruckPosition(r);
      setTruckPosition(r.truck_id, at.lat, at.lng);
    });
}

function bool(value: string | undefined): boolean | undefined {
  if (value === undefined) return undefined;
  return value === 'true' || value === '1';
}

export const recyclerRoutes: DemoRouteDef[] = [
  // ── Trucks and drivers ──
  ['get', '/truck/detail/:truckId', ({ params }) => {
    ensureDemoData();
    refreshLivePositions();
    const truck = truckRecordById(params.truckId);
    return truck ? { result: truck } : notFound('Truck');
  }],
  ['get', '/truck/driver/:driverId/truck', ({ params }) => {
    ensureDemoData();
    // A driver without a truck answers with an empty result (the Drivers page treats it as "unassigned").
    return { result: truckForDriver(params.driverId) ?? null };
  }],
  ['get', '/truck/driver/:driverId', ({ params }) => {
    ensureDemoData();
    const user = userById(params.driverId);
    if (!user) return notFound('Driver');
    return {
      result: {
        driver: { id: params.driverId, username: driverName(params.driverId) ?? user.full_name ?? user.username, phone: user.phone ?? null },
        truck: truckForDriver(params.driverId) ?? null,
        historical_orders: driverHistory(params.driverId),
      },
    };
  }],
  ['post', '/truck/driver/register', ({ body }) => {
    ensureDemoData();
    const b = bodyOf(body);
    if (!b.username || !b.password) return demoError(422, 'Username and password are required');
    if (b.password !== b.password_confirm) return demoError(422, 'Passwords do not match');
    if (usernameTaken(String(b.username))) return demoError(409, 'Username already exists');
    const user = registerDriverUser(b);
    if (typeof b.truck_id === 'string' && b.truck_id) assignDriver(b.truck_id, user.id as string);
    return { success: true, result: user };
  }],
  ['put', '/truck/:truckId/assign-driver', ({ params, body }) => {
    ensureDemoData();
    const driverId = String(bodyOf(body).driver_id ?? '');
    if (!userById(driverId)) return notFound('Driver');
    const truck = assignDriver(params.truckId, driverId);
    return truck ? { success: true, result: truck } : notFound('Truck');
  }],
  ['put', '/truck/:truckId/unassign-driver', ({ params }) => {
    ensureDemoData();
    const truck = unassignDriver(params.truckId);
    return truck ? { success: true, result: truck } : notFound('Truck');
  }],
  ['get', '/truck/:orgId', (ctx) => {
    ensureDemoData();
    refreshLivePositions();
    const status = q(ctx, 'status');
    const search = q(ctx, 'search');
    const rows = listTrucks(ctx.params.orgId)
      .filter((t) => !status || t.status === status)
      .filter((t) => includesText([t.name, t.data?.en_plate_number, t.data?.ar_plate_number, t.data?.car_model], search));
    const page = paginate(rows, ctx.query.page, ctx.query.limit);
    return { result: page.items, total: page.total, page: page.page, limit: page.limit };
  }],
  ['post', '/truck', ({ body }) => {
    ensureDemoData();
    return { result: createTruck(bodyOf(body)) };
  }],
  ['put', '/truck/:truckId', ({ params, body }) => {
    ensureDemoData();
    const truck = updateTruck(params.truckId, bodyOf(body));
    return truck ? { result: truck } : notFound('Truck');
  }],
  ['delete', '/truck/:truckId', ({ params }) => {
    ensureDemoData();
    return deleteTruck(params.truckId) ? { result: { success: true } } : notFound('Truck');
  }],

  // ── Route schedules ──
  ['get', '/route_schedule/organization/:orgId', ({ params }) => {
    ensureDemoData();
    return { result: organizationSchedule(params.orgId) };
  }],
  ['get', '/route_schedule/truck/:truckId', ({ params }) => {
    ensureDemoData();
    return truckRecordById(params.truckId) ? { result: truckSchedule(params.truckId) } : notFound('Truck');
  }],
  ['put', '/route_schedule/truck/:truckId/day/:day', ({ params, body }) => {
    ensureDemoData();
    if (!truckRecordById(params.truckId)) return notFound('Truck');
    if (!isDayOfWeek(params.day)) return demoError(422, 'Invalid day_of_week');
    const routeIds = bodyOf(body).route_ids;
    if (!Array.isArray(routeIds) || routeIds.length === 0) return demoError(422, 'route_ids must contain at least one route');
    return { result: setScheduleDay(params.truckId, params.day, routeIds as string[]) };
  }],
  ['delete', '/route_schedule/truck/:truckId/day/:day', ({ params }) => {
    ensureDemoData();
    if (!isDayOfWeek(params.day)) return demoError(422, 'Invalid day_of_week');
    clearScheduleDay(params.truckId, params.day);
    return { ok: true };
  }],

  // ── Suppliers ──
  ['get', '/ops/suppliers/organization-options', (ctx) => {
    ensureDemoData();
    const search = q(ctx, 'search');
    const purpose = q(ctx, 'purpose');
    const current = q(ctx, 'organization_id') ?? ctx.orgId;
    const rows = listOrganizations()
      .filter((o) => o.id !== current)
      .filter((o) => (purpose === 'share_target' ? o.organization_type !== 'waste_outlet' : true))
      .filter((o) => includesText([o.name, String(o.name_ar ?? '')], search))
      .map((o) => ({ id: o.id, name: o.name, organization_type: o.organization_type }));
    return { result: rows };
  }],
  ['get', '/ops/suppliers/import-template', () => new Blob(['name,contact_name,contact_phone,city\n'], { type: 'text/csv' })],
  ['post', '/ops/suppliers/import', () => ({ result: { total_rows: 0, created: 0, failed: [] } })],
  ['get', '/ops/suppliers', (ctx) => {
    ensureDemoData();
    if (!isRecycler(ctx)) return { result: { suppliers: [], total: 0, page: 1, limit: Number(ctx.query.limit ?? 20) || 20 } };
    const status = q(ctx, 'status');
    const search = q(ctx, 'search');
    const linked = bool(q(ctx, 'linked'));
    const materialId = q(ctx, 'material_id');
    const rows = listSupplierRecords()
      .filter((s) => !status || s.status === status)
      .filter((s) => linked === undefined || Boolean(s.supplier_org_id) === linked)
      .filter((s) => !materialId || s.material_ids.includes(materialId))
      .map((s) => serializeSupplier(s, false))
      .filter((s) => includesText([s.name, s.linked_organization_name, ...s.contacts.map((c) => c.name)], search));
    const page = paginate(rows, ctx.query.page, ctx.query.limit);
    return { result: { suppliers: page.items, total: page.total, page: page.page, limit: page.limit } };
  }],
  ['post', '/ops/suppliers', ({ body }) => {
    ensureDemoData();
    const b = bodyOf(body);
    if (!String(b.name ?? '').trim()) return demoError(422, 'Name is required');
    return { result: serializeSupplier(createSupplier(b), true) };
  }],
  ['post', '/ops/suppliers/:supplierId/share', ({ params, body }) => {
    ensureDemoData();
    const source = supplierRecordById(params.supplierId);
    if (!source) return notFound('Supplier');
    const target = String(bodyOf(body).target_owner_org_id ?? '');
    return {
      result: {
        ...serializeSupplier(source, false),
        id: ids.supplier(0x900 + (hashString(`${target}:${source.id}`) % 0xff)),
        owner_org_id: target,
        source: 'shared' as const,
      },
    };
  }],
  ['post', '/ops/suppliers/:supplierId/sync-collection-sites', ({ params }) => {
    ensureDemoData();
    const supplier = supplierRecordById(params.supplierId);
    if (!supplier) return notFound('Supplier');
    if (!supplier.supplier_org_id) return demoError(422, 'This supplier is not linked to a Recyclee outlet');
    const sites = listSites(supplier.supplier_org_id).map((s) => {
      const address = (s.data?.address ?? {}) as Record<string, string>;
      const contact = (s.data?.contact_info ?? {}) as Record<string, string>;
      const materials = Array.from(new Set(listContainerRecords(DEMO_OUTLET_ORG_ID).filter((c) => c.collection_site_id === s.id).map((c) => c.material_type_id)));
      return {
        id: s.id,
        name: String(s.data?.name ?? s.id),
        latitude: s.latitude,
        longitude: s.longitude,
        address: address.street ? `${address.street}, ${address.city ?? ''}`.trim() : null,
        city: address.city ?? null,
        contact: { name: contact.name, phone: contact.phone, email: contact.email },
        materialIds: materials.length ? materials : supplier.material_ids,
      };
    });
    return { result: syncSupplierSites(supplier.id, sites) };
  }],
  ['post', '/ops/suppliers/:supplierId/agreement/prices', ({ params, body }) => {
    ensureDemoData();
    if (!supplierRecordById(params.supplierId)) return notFound('Supplier');
    const b = bodyOf(body);
    if (!b.material_id || !(Number(b.price) >= 0)) return demoError(422, 'material_id and a valid price are required');
    return { result: setAgreementPrice(params.supplierId, b) };
  }],
  ['get', '/ops/suppliers/:supplierId/agreement/prices', ({ params }) => {
    ensureDemoData();
    return supplierRecordById(params.supplierId) ? { result: listAgreementPrices(params.supplierId) } : notFound('Supplier');
  }],
  ['post', '/ops/suppliers/:supplierId/agreement/documents', ({ params, body }) => {
    ensureDemoData();
    if (!supplierRecordById(params.supplierId)) return notFound('Supplier');
    return { result: attachAgreementDocument(params.supplierId, bodyOf(body)) };
  }],
  ['get', '/ops/suppliers/:supplierId/agreement/documents', ({ params }) => {
    ensureDemoData();
    return supplierRecordById(params.supplierId) ? { result: listAgreementDocuments(params.supplierId) } : notFound('Supplier');
  }],
  ['delete', '/ops/suppliers/:supplierId/agreement/documents/:documentId', ({ params }) => {
    ensureDemoData();
    return archiveAgreementDocument(params.supplierId, params.documentId) ? { result: { success: true } } : notFound('Document');
  }],
  ['get', '/ops/suppliers/:supplierId', ({ params }) => {
    ensureDemoData();
    const s = supplierRecordById(params.supplierId);
    return s && s.status !== 'archived' ? { result: serializeSupplier(s, true) } : notFound('Supplier');
  }],
  ['patch', '/ops/suppliers/:supplierId', ({ params, body }) => {
    ensureDemoData();
    const s = updateSupplier(params.supplierId, bodyOf(body));
    return s ? { result: serializeSupplier(s, true) } : notFound('Supplier');
  }],
  ['delete', '/ops/suppliers/:supplierId', ({ params }) => {
    ensureDemoData();
    return archiveSupplier(params.supplierId) ? { result: { success: true } } : notFound('Supplier');
  }],

  // ── Collection points ──
  ['get', '/ops/collection-points/import-template', () => new Blob(['name,contact_name,contact_phone,city\n'], { type: 'text/csv' })],
  ['post', '/ops/collection-points/import', () => ({ result: { total_rows: 0, created: 0, failed: [] } })],
  ['post', '/ops/collection-points/parse-maps-link', ({ body }) => {
    const url = String(bodyOf(body).url ?? '');
    const m = /(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/.exec(decodeURIComponent(url)) ?? /@(-?\d{1,2}\.\d+),(-?\d{1,3}\.\d+)/.exec(url);
    if (!m) return demoError(422, 'Could not find coordinates in that link (try a link with "@lat,lng" or "?q=lat,lng")');
    return { result: { latitude: Number(m[1]), longitude: Number(m[2]) } };
  }],
  ['get', '/ops/collection-points', (ctx) => {
    ensureDemoData();
    if (!isRecycler(ctx)) return { result: { collection_points: [], total: 0, page: 1, limit: Number(ctx.query.limit ?? 20) || 20 } };
    const supplierId = q(ctx, 'supplier_id');
    const zoneId = q(ctx, 'zone_id');
    const status = q(ctx, 'status');
    const materialId = q(ctx, 'material_id');
    const city = q(ctx, 'city');
    const search = q(ctx, 'search');
    const zone = zoneId ? zoneRecordById(zoneId) : undefined;
    const rows = listPointRecords()
      .filter((p) => (status ? p.status === status : p.status !== 'archived'))
      .filter((p) => !supplierId || p.supplier_id === supplierId)
      .filter((p) => !materialId || p.material_ids.includes(materialId))
      .filter((p) => !city || p.city === city)
      .map(serializePoint)
      .filter((p) => !zone || (p.labels ?? []).some((l) => l.id === zone.id))
      .filter((p) => includesText([p.name, p.address, p.city, p.contact_name], search));
    const page = paginate(rows, ctx.query.page, ctx.query.limit);
    return { result: { collection_points: page.items, total: page.total, page: page.page, limit: page.limit } };
  }],
  ['post', '/ops/collection-points', ({ body }) => {
    ensureDemoData();
    const b = bodyOf(body);
    if (!supplierRecordById(String(b.supplier_id ?? ''))) return demoError(422, 'Choose a supplier for the collection point');
    return { result: serializePoint(createPoint(b)) };
  }],
  ['patch', '/ops/collection-points/:pointId', ({ params, body }) => {
    ensureDemoData();
    const p = updatePoint(params.pointId, bodyOf(body));
    return p ? { result: serializePoint(p) } : notFound('Collection point');
  }],
  ['delete', '/ops/collection-points/:pointId', ({ params }) => {
    ensureDemoData();
    return archivePoint(params.pointId) ? { result: { success: true } } : notFound('Collection point');
  }],

  // ── Labels (zones) ──
  ['get', '/ops/zones/:zoneId/collection-points', ({ params }) => {
    ensureDemoData();
    const zone = zoneRecordById(params.zoneId);
    if (!zone) return notFound('Label');
    const points = listPointRecords()
      .filter((p) => p.status !== 'archived')
      .map(serializePoint)
      .filter((p) => (p.labels ?? []).some((l) => l.id === zone.id));
    return { result: { zone: serializeZone(zone), collection_points: points } };
  }],
  ['get', '/ops/zones', (ctx) => {
    ensureDemoData();
    if (!isRecycler(ctx)) return { result: { zones: [], total: 0, page: 1, limit: 100 } };
    const search = q(ctx, 'search');
    const rows = listZoneRecords().map(serializeZone).filter((z) => includesText([z.name], search));
    const page = paginate(rows, ctx.query.page, ctx.query.limit);
    return { result: { zones: page.items, total: page.total, page: page.page, limit: page.limit } };
  }],
  ['post', '/ops/zones', ({ body }) => {
    ensureDemoData();
    const b = bodyOf(body);
    if (!Array.isArray(b.boundary) || b.boundary.length < 4) return demoError(422, 'Draw a closed boundary with at least three corners');
    return { result: serializeZone(createZone(b)) };
  }],
  ['patch', '/ops/zones/:zoneId', ({ params, body }) => {
    ensureDemoData();
    const z = updateZone(params.zoneId, bodyOf(body));
    return z ? { result: serializeZone(z) } : notFound('Label');
  }],
  ['delete', '/ops/zones/:zoneId', ({ params }) => {
    ensureDemoData();
    return archiveZone(params.zoneId) ? { result: { success: true } } : notFound('Label');
  }],

  // ── Facilities ──
  ['get', '/ops/facilities/:facilityId/incoming-orders', ({ params, query }) => {
    ensureDemoData();
    if (!facilityRecordById(params.facilityId)) return notFound('Facility');
    return { result: facilityIncoming(params.facilityId, query.page, query.limit) };
  }],
  ['get', '/ops/facilities', (ctx) => {
    ensureDemoData();
    if (!isRecycler(ctx)) return { result: { facilities: [], total: 0, page: 1, limit: Number(ctx.query.limit ?? 20) || 20 } };
    const type = q(ctx, 'type');
    const status = q(ctx, 'status');
    const city = q(ctx, 'city');
    const search = q(ctx, 'search');
    const rows = listFacilityRecords()
      .filter((f) => !type || f.type === type)
      .filter((f) => !status || f.status === status)
      .filter((f) => !city || f.city === city)
      .map(serializeFacility)
      .filter((f) => includesText([f.name, f.address, f.city], search));
    const page = paginate(rows, ctx.query.page, ctx.query.limit);
    return { result: { facilities: page.items, total: page.total, page: page.page, limit: page.limit } };
  }],
  ['get', '/ops/facilities/:facilityId', ({ params }) => {
    ensureDemoData();
    const f = facilityRecordById(params.facilityId);
    return f && f.status !== 'archived' ? { result: serializeFacility(f) } : notFound('Facility');
  }],
  ['post', '/ops/facilities', ({ body }) => {
    ensureDemoData();
    return { result: serializeFacility(createFacility(bodyOf(body))) };
  }],
  ['patch', '/ops/facilities/:facilityId', ({ params, body }) => {
    ensureDemoData();
    const f = updateFacility(params.facilityId, bodyOf(body));
    return f ? { result: serializeFacility(f) } : notFound('Facility');
  }],
  ['delete', '/ops/facilities/:facilityId', ({ params }) => {
    ensureDemoData();
    return archiveFacility(params.facilityId) ? { result: { success: true } } : notFound('Facility');
  }],

  // ── Routes ──
  ['get', '/ops/routes', (ctx) => {
    ensureDemoData();
    if (!isRecycler(ctx)) return { result: { routes: [], total: 0, page: 1, limit: Number(ctx.query.limit ?? 20) || 20 } };
    const city = q(ctx, 'city');
    const status = q(ctx, 'status');
    const truckId = q(ctx, 'truck_id');
    const search = q(ctx, 'search');
    const rows = listRouteRecords()
      .filter((r) => !city || r.city === city)
      .filter((r) => !status || r.status === status)
      .filter((r) => !truckId || r.assigned_truck_id === truckId)
      .map(serializeRoute)
      .filter((r) => includesText([r.name, r.city, r.assigned_truck?.name], search));
    const page = paginate(rows, ctx.query.page, ctx.query.limit);
    return { result: { routes: page.items, total: page.total, page: page.page, limit: page.limit } };
  }],
  ['get', '/ops/routes/:routeId', ({ params }) => {
    ensureDemoData();
    const r = routeRecordById(params.routeId);
    return r && r.status !== 'archived' ? { result: serializeRoute(r) } : notFound('Route');
  }],
  ['post', '/ops/routes', ({ body }) => {
    ensureDemoData();
    const b = bodyOf(body);
    const stops = (Array.isArray(b.stops) ? b.stops : Array.isArray(b.collection_point_ids) ? b.collection_point_ids : []) as unknown[];
    if (!String(b.name ?? '').trim() || stops.length === 0) return demoError(422, 'A route needs a name and at least one stop');
    return { result: serializeRoute(createRoute(b)) };
  }],
  ['patch', '/ops/routes/:routeId', ({ params, body }) => {
    ensureDemoData();
    const r = updateRoute(params.routeId, bodyOf(body));
    return r ? { result: serializeRoute(r) } : notFound('Route');
  }],
  ['delete', '/ops/routes/:routeId', ({ params }) => {
    ensureDemoData();
    return archiveRoute(params.routeId) ? { result: { success: true } } : notFound('Route');
  }],
  ['put', '/ops/routes/:routeId/assign-truck', ({ params, body }) => {
    ensureDemoData();
    const truckId = String(bodyOf(body).truck_id ?? '');
    if (!truckRecordById(truckId)) return notFound('Truck');
    const r = setRouteTruck(params.routeId, truckId);
    return r ? { result: serializeRoute(r) } : notFound('Route');
  }],
  ['put', '/ops/routes/:routeId/unassign-truck', ({ params }) => {
    ensureDemoData();
    const r = setRouteTruck(params.routeId, null);
    return r ? { result: serializeRoute(r) } : notFound('Route');
  }],

  // ── Completion flows ──
  ['get', '/ops/collection-flows/step-types', () => ({ result: FLOW_STEP_TYPES })],
  ['get', '/ops/collection-flows/contract', (ctx) => {
    const flowType = (q(ctx, 'flow_type') ?? 'supplier_collection') as FlowType;
    const contract = FLOW_CONTRACTS[flowType];
    return contract ? { result: contract } : demoError(422, 'Unknown flow_type');
  }],
  ['get', '/ops/collection-flows/active', (ctx) => {
    ensureDemoData();
    const flowType = (q(ctx, 'flow_type') ?? 'supplier_collection') as FlowType;
    return { result: resolveFlow(flowType, { supplier_id: q(ctx, 'supplier_id'), facility_id: q(ctx, 'facility_id') }) };
  }],
  ['get', '/ops/collection-flows', (ctx) => {
    ensureDemoData();
    return {
      result: listFlows({ supplier_id: q(ctx, 'supplier_id'), facility_id: q(ctx, 'facility_id'), include_superseded: bool(q(ctx, 'include_superseded')) }),
    };
  }],
  ['put', '/ops/collection-flows/draft', ({ body }) => {
    ensureDemoData();
    return { result: saveFlowDraft(bodyOf(body)) };
  }],
  ['post', '/ops/collection-flows/:flowId/activate', ({ params }) => {
    ensureDemoData();
    const res = activateFlow(params.flowId);
    if (!res) return notFound('Flow');
    if ('missing' in res) return demoError(422, `Cannot activate: required steps are missing or optional: ${res.missing.join(', ')}`);
    return { result: res };
  }],

  // ── Pre-orders monitor ──
  ['get', '/dashboard/recycler/:orgId/pre-orders', ({ params, query }) => {
    ensureDemoData();
    return { result: preOrdersMonitor(params.orgId, query.offset, query.limit) };
  }],
];
